import { useQuery } from '@tanstack/react-query'
import { fetchMenuPoints } from '../lib/requests/fetch-menu-points'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import useSearchParamsHelper from './use-search-params'

export const useMenuPoints = () => {
  const menuPoints = useQuery(['menuPoints'], () => fetchMenuPoints())
  const [params] = useSearchParams()
  const {
    activeTopic,
    activeCategory,
    activeSubCategory,
    activeMaterial,
    setActiveTopic,
    setActiveCategory,
    setActiveSubCategory,
    setActiveMaterial
  } = useSearchParamsHelper()

  const getMenuPoints = (topicId: string | null, categoryId: string | null) => {
    if (menuPoints?.isFetched) {
      if (!topicId) return menuPoints.data?.data.topics || []

      if (!categoryId)
        return menuPoints.data?.data?.topics?.find((d) => d.id === topicId)?.categories || []

      if (categoryId)
        return (
          menuPoints.data?.data?.topics
            ?.find((d) => d.id === topicId)
            ?.categories.find((category) => category.id === categoryId)?.subCategories || []
        )
    }
    return []
  }

  useEffect(() => {
    if (!menuPoints.isFetched || !menuPoints.data?.data.topics) {
      return
    }

    // Process URL parameters in the correct order
    const tema = params.get('tema')
    const category = params.get('category')
    const subcategory = params.get('subcategory')
    const material = params.get('material')

    if (tema && (!activeTopic || activeTopic.id != tema)) {
      const foundTopic = menuPoints.data?.data.topics.find((d) => d.id == tema)
      if (foundTopic) {
        // When loading from URL, don't clear category/subcategory if they exist in URL
        const shouldClearChildren = !category
        setActiveTopic(tema, foundTopic.title, foundTopic.color, shouldClearChildren)
      }
    }

    if (tema && category && (!activeCategory || activeCategory.id != category)) {
      const foundCategory = getMenuPoints(tema, null).find((d) => d.id == category)
      if (foundCategory) {
        setActiveCategory(category, foundCategory.title)
      }
    }

    if (
      tema &&
      category &&
      subcategory &&
      (!activeSubCategory || activeSubCategory.id != subcategory)
    ) {
      const foundSubCategory = getMenuPoints(tema, category).find((d) => d.id == subcategory)
      if (foundSubCategory) {
        setActiveSubCategory(subcategory, foundSubCategory.title)
      }
    }

    if (material && (!activeMaterial || activeMaterial.id != material)) {
      // Set the material parameter - the actual material will be loaded when materials are fetched
      setActiveMaterial(material, '') // Title will be set when material is found
    }
  }, [
    menuPoints.isFetched,
    menuPoints.data,
    params,
    setActiveTopic,
    setActiveCategory,
    setActiveSubCategory,
    setActiveMaterial,
    getMenuPoints
  ])

  return {
    menuPoints,
    getMenuPoints,
    activeTopic,
    activeCategory,
    activeSubCategory,
    activeMaterial,
    setActiveTopic,
    setActiveCategory,
    setActiveSubCategory,
    setActiveMaterial
  }
}
